import React, { useState, useRef, useEffect } from 'react';
import { Mic, Volume2, Play } from 'lucide-react';

const colorHex = {
  'красный': '#ef4444',
  'синий': '#3b82f6',
  'зеленый': '#22c55e',
  'желтый': '#eab308'
};

const colorImg = {
  'красный': 'https://4da21c74-3916-49d9-84a3-03b7f1220048.selstorage.ru/red.png',
  'синий': 'https://4da21c74-3916-49d9-84a3-03b7f1220048.selstorage.ru/blue.png',
  'зеленый': 'https://4da21c74-3916-49d9-84a3-03b7f1220048.selstorage.ru/green.png',
  'желтый': 'https://4da21c74-3916-49d9-84a3-03b7f1220048.selstorage.ru/yellow.png'
};

// Поля пилота: врач отмечает перед тестом. Нужны для отбора контрольной
// группы и учёта факторов, искажающих результат (ревью: Scarpina & Tagini 2017).
// Значение 'unknown' = не проверялось; это не то же самое, что «норма».
const PILOT_FIELDS = [
  { key: 'colorVision', label: 'Цветовое зрение', options: [['normal', 'Норма'], ['impaired', 'Нарушено'], ['unknown', 'Не проверялось']] },
  { key: 'speech', label: 'Речь', options: [['normal', 'Норма'], ['dysarthria', 'Дизартрия'], ['aphasia', 'Афазия'], ['unknown', 'Не оценивалась']] },
  { key: 'corrected', label: 'Зрение (очки/линзы, если нужны)', options: [['yes', 'Скорректировано / не нужно'], ['no', 'Не скорректировано'], ['unknown', 'Не выяснялось']] },
  { key: 'fatigue', label: 'Утомление или сонливость', options: [['no', 'Нет'], ['yes', 'Есть'], ['unknown', 'Не выяснялось']] },
  { key: 'sedatives', label: 'Седативные препараты за последние сутки', options: [['no', 'Нет'], ['yes', 'Да'], ['unknown', 'Не выяснялось']] },
];
const PILOT_DEFAULT = { colorVision: 'unknown', speech: 'unknown', corrected: 'unknown', fatigue: 'unknown', sedatives: 'unknown', nativeLang: '' };

const wordToColor = (word) => {
  const w = word.toLowerCase().replace(/[.,!?]/g, '');
  if (w.includes('красн')) return 'красный';
  if (w.includes('син')) return 'синий';
  if (w.includes('зелен') || w.includes('зелён')) return 'зеленый';
  if (w.includes('желт') || w.includes('жёлт')) return 'желтый';
  return null;
};

// ПОДСЧЁТ v2: клиент НЕ судит об ошибках. Он только пишет ленту
// финализированных слов с метками времени. Ошибки считает выравнивание
// последовательностей (alignOps) в отчёте/на бэкенде — каскад ложных
// ошибок невозможен по построению.
export default function Stroop({ question, transcript, finalTranscript, status, speak, playAudio, startListening, stopAll, forceFinishRef, onChoiceClick }) {
  const { cards } = question;
  const [currentCard, setCurrentCard] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState('pilot');   // pilot → instruction → running (на каждую карточку)
  const [pilot, setPilot] = useState(PILOT_DEFAULT);
  const pilotRef = useRef(PILOT_DEFAULT);
  // Диагностика распознавания: что Chrome реально вернул текстом (в ленту
  // попадают только слова-цвета; остальное отбрасывается). Нужно, чтобы
  // отличить «слово не услышано» от «услышано как другое слово».
  const lastFinalTextRef = useRef('');
  const lastInterimTextRef = useRef('');
  const asrTextRef = useRef([null, null, null]);
  lastFinalTextRef.current = finalTranscript || '';
  lastInterimTextRef.current = transcript || '';

  const startTimeRef = useRef(0);
  const currentCardRef = useRef(0);
  const isFinishedRef = useRef(false);
  const onChoiceClickRef = useRef(onChoiceClick);
  const speakRef = useRef(speak);
  const playAudioRef = useRef(playAudio);
  const startListeningRef = useRef(startListening);

  const heardRef = useRef([[], [], []]);   // финальные слова: [{c, batchT, batchId}]
  const committedFinalRef = useRef(0);     // сколько финальных слов уже в ленте
  const committedDisplayRef = useRef(0);   // для экранного счётчика
  const batchIdRef = useRef(0);            // номер финальной пачки Chrome
  const timelineRef = useRef([[], [], []]); // интерим-таймлайн: [[мс от «Начать», слов], ...]
  const maxSeenRef = useRef(0);            // бегущий максимум цветов по интериму
  const lastActivityAtRef = useRef(0);     // активность: интерим ИЛИ финал (для idle)
  const wallMsRef = useRef([0, 0, 0]);
  const idleWaitMsRef = useRef([0, 0, 0]);
  const transitioningRef = useRef(false);
  const endedByRef = useRef(['auto', 'auto', 'auto']);
  const timesRef = useRef([0, 0, 0]);
  const thresholdAtRef = useRef(0);        // момент достижения порога авто-завершения

  useEffect(() => { currentCardRef.current = currentCard; }, [currentCard]);
  useEffect(() => { speakRef.current = speak; }, [speak]);
  useEffect(() => { playAudioRef.current = playAudio; }, [playAudio]);
  useEffect(() => { startListeningRef.current = startListening; }, [startListening]);
  useEffect(() => { onChoiceClickRef.current = onChoiceClick; }, [onChoiceClick]);

    const finishCard = (mode) => {
    if (transitioningRef.current || isFinishedRef.current) return;
    transitioningRef.current = true;
    const cCard = currentCardRef.current;
    endedByRef.current[cCard] = mode;
    asrTextRef.current[cCard] = {
      final: String(lastFinalTextRef.current).slice(0, 2000),
      interim: String(lastInterimTextRef.current).slice(0, 2000),
    };

    const nowMs = Date.now();
    wallMsRef.current[cCard] = nowMs - startTimeRef.current;
    // idle-закрытие: вычитаем время простоя после последней активности
    idleWaitMsRef.current[cCard] = mode === 'idle' ? nowMs - lastActivityAtRef.current : 0;
        console.log(`[TIMELINE] карточка ${cCard}: ${timelineRef.current[cCard].length} наблюдений, первое @${Math.round((timelineRef.current[cCard][0]?.[0] || 0) / 1000)}s, последнее @${Math.round((timelineRef.current[cCard][timelineRef.current[cCard].length - 1]?.[0] || 0) / 1000)}s`);
    timesRef.current[cCard] = Math.round(wallMsRef.current[cCard] / 1000); // совместимость

    if (cCard < 2) {
      setCurrentCard(cCard + 1);
      setCurrentIndex(0);
      setPhase('instruction');
    } else {
      const payload = JSON.stringify({
        times: timesRef.current,                 // секунды, целые (legacy)
        wallMs: wallMsRef.current,               // миллисекунды, точно
        idleWaitMs: idleWaitMsRef.current,       // сколько ждали тишины
        heard: heardRef.current,                 // [{c, batchT, batchId}]
        timeline: timelineRef.current,           // интерим-наблюдения [мс, слов]
        endedBy: endedByRef.current,
        // interference и speechTimes КЛИЕНТ НЕ СЧИТАЕТ — производные
        // считает сервер по сырью (ревью stroop-web-2)
        expected: cards.map(c => (c.expected || []).slice()),
        // что НАПЕЧАТАНО на карточке (для карточки B — квадраты, слов нет).
        // Нужно серверу, чтобы отличить «прочитал слово» от прочих ошибок.
        printed: cards.map(c => (c.type === 'squares' ? [] : c.data.map(d => d.word))),
        asrText: asrTextRef.current,   // диагностика: сырой текст распознавания по карточкам
        // поля пилота + техническая среда (автоматически)
        pilot: {
          ...pilotRef.current,
          browser: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 120),
          engine: 'chrome-webspeech',
        },
      });
      isFinishedRef.current = true;
      onChoiceClickRef.current(payload);
    }
  };

    const handleStart = () => {
    if (stopAll) stopAll(); // гасим хвост озвучки — микрофон не должен слышать синтез
    startTimeRef.current = Date.now();
    setPhase('running');
    committedFinalRef.current = 0;
    committedDisplayRef.current = 0;
    transitioningRef.current = false;
    maxSeenRef.current = 0;
    timelineRef.current[currentCardRef.current] = [];
    thresholdAtRef.current = 0;
    lastActivityAtRef.current = Date.now();
    startListeningRef.current();
  };

  // ScaleRunner зовёт это при «Завершить досрочно»: карточка закрывается
  // штатно, с полным payload, вместо потери всего теста.
  useEffect(() => {
    forceFinishRef.current = (mode) => {
      if (phase === 'pilot') return;   // на форме пилота карточка ещё не начата
      finishCard(mode || 'manual');
    };
    return () => { forceFinishRef.current = null; };
  });

  // 1. Озвучка инструкции
  useEffect(() => {
    if (status === 'show_options' && phase === 'instruction') {
      const card = cards[currentCard];
      console.log('[STROOP-AUDIO] карточка', currentCard, '| аудио:', card.instruction_audio_url, '| playAudio есть:', typeof playAudioRef.current);
      const instructionText = `Карточка ${String.fromCharCode(65 + currentCard)}. ${card.instruction}`;
      if (card.instruction_audio_url && playAudioRef.current) {
        playAudioRef.current(card.instruction_audio_url, () => {});
      } else if (speakRef.current) {
        speakRef.current(instructionText, () => {});
      }
    }
  }, [status, phase, currentCard, cards]);

    // 2. Лента финалов. Время внутри пачки НЕ выдумываем: все слова пачки
  // получают общий batchT (момент прихода финального результата Chrome).
  // Реальный пер-словный темп даёт интерим-таймлайн (эффект №3).
  useEffect(() => {
    if (phase !== 'running') return;
    if (transitioningRef.current) return;
    if (!finalTranscript) return;

    const words = finalTranscript.toLowerCase().split(/[^а-яё]+/).filter(Boolean);
    const colors = words.map(wordToColor).filter(Boolean);
    const newColors = colors.slice(committedFinalRef.current);
    if (newColors.length === 0) return;

    committedFinalRef.current = colors.length;
    lastActivityAtRef.current = Date.now();

    const cCard = currentCardRef.current;
    batchIdRef.current += 1;
    const batchT = Math.round(((Date.now() - startTimeRef.current) / 1000) * 10) / 10;
    for (const color of newColors) {
      heardRef.current[cCard].push({ c: color, batchT, batchId: batchIdRef.current });
    }
    console.log(`[STROOP] финалов в ленте: ${colors.length} (+${newColors.length}), пачка #${batchIdRef.current} @${batchT}s, карточка ${cCard}`);

    // Порог достигнут — но не закрываем сразу: ещё ~1.5 с, чтобы дошли
    // финалы последних слов и лишние слова (повторы) не отсекли хвост.
    // Порог авто-завершения — тоже по интерим-максимуму: если речь
    // договорена (maxSeen == total), начинаем окно доезда финалов,
    // даже если Chrome ещё не финализировал хвост.
    if (maxSeenRef.current >= cards[cCard].data.length && thresholdAtRef.current === 0) {
      thresholdAtRef.current = Date.now();
    }
  }, [finalTranscript, phase, cards]);

  // 3. Интерим-таймлайн: моменты роста числа услышанных цветов. Для
  // подсчёта ошибок интерим не годится (слова пересматриваются), но для
  // ВРЕМЕНИ идеален: факт роста счётчика — наблюдаемый момент речи.
  useEffect(() => {
    if (phase !== 'running' || transitioningRef.current || !transcript) return;
    const words = transcript.toLowerCase().split(/[^а-яё]+/).filter(Boolean);
    const colors = words.map(wordToColor).filter(Boolean);
    if (colors.length > maxSeenRef.current) {
      maxSeenRef.current = colors.length;
      const now = Date.now();
      lastActivityAtRef.current = now;
      timelineRef.current[currentCardRef.current].push([now - startTimeRef.current, colors.length]);
      setCurrentIndex(Math.min(colors.length, cards[currentCardRef.current].data.length));
    }
  }, [transcript, phase, cards]);

  // 4. Автозавершение: порог слов + пауза, ИЛИ долгая тишина при ≥80%.
  // «Активность» = интерим или финал: idle по финалам преждевременно
  // закрывал карточку, пока Chrome копил большую пачку.
  useEffect(() => {
    if (phase !== 'running') return;
    const iv = setInterval(() => {
      if (transitioningRef.current) return;
      const cCard = currentCardRef.current;
      const heard = heardRef.current[cCard].length;
      const total = cards[cCard].data.length;

      // Прогресс — по интерим-максимуму (maxSeen): финалы Chrome могут
      // терять до половины слов в плохой день, и порог по финалам вешал
      // карточку навсегда при дочитанной речи.
      const progress = Math.max(heard, maxSeenRef.current);

      // порог пройден и хватило времени на доезд финалов — закрываем
      if (progress >= total && thresholdAtRef.current > 0 && Date.now() - thresholdAtRef.current > 1500) {
        finishCard('auto');
        return;
      }
      // 80% слов + тишина — штатный idle. ЛИБО ≥50% слов и очень долгая
      // тишина (12 с): Chrome потерял хвост, но пациент точно закончил —
      // закрываем, иначе карточка висит вечно (случай 60/100).
      if (progress >= Math.floor(total * 0.8) && Date.now() - lastActivityAtRef.current > 5000) {
        finishCard('idle');
      } else if (maxSeenRef.current >= Math.floor(total * 0.5) && Date.now() - lastActivityAtRef.current > 12000) {
        finishCard('idle');
      }
    }, 500);
    return () => clearInterval(iv);
  }, [phase, cards]);

  if (phase === 'pilot') {
    const setField = (key, value) => {
      const next = { ...pilotRef.current, [key]: value };
      pilotRef.current = next;
      setPilot(next);
    };
    return (
      <div className="flex flex-col items-center gap-5 w-full max-w-xl h-screen justify-center">
        <h2 className="text-2xl font-bold text-primary text-center">Перед тестом</h2>
        <p className="text-sm text-center text-muted-foreground">
          Отметьте то, что известно. «Не проверялось» — допустимый ответ. Эти данные нужны, чтобы корректно сравнивать результаты.
        </p>
        <div className="w-full flex flex-col gap-3 bg-secondary/50 rounded-2xl border border-border p-5">
          {PILOT_FIELDS.map(f => (
            <label key={f.key} className="flex items-center justify-between gap-4 text-sm">
              <span className="text-primary font-medium">{f.label}</span>
              <select
                value={pilot[f.key]}
                onChange={e => setField(f.key, e.target.value)}
                className="bg-background border border-border rounded-lg px-2 py-1.5 text-sm max-w-[55%]"
              >
                {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          ))}
          <label className="flex items-center justify-between gap-4 text-sm">
            <span className="text-primary font-medium">Родной язык</span>
            <input
              type="text"
              value={pilot.nativeLang}
              maxLength={30}
              onChange={e => setField('nativeLang', e.target.value)}
              placeholder="не указан"
              className="bg-background border border-border rounded-lg px-2 py-1.5 text-sm w-[55%]"
            />
          </label>
        </div>
        <button onClick={() => setPhase('instruction')} className="px-8 py-4 bg-green-500 text-white rounded-2xl font-bold text-lg flex items-center gap-2 hover:bg-green-600">
          <Play /> Продолжить
        </button>
      </div>
    );
  }

  if (phase === 'instruction') {
    return (
      <div className="flex flex-col items-center gap-6 w-full max-w-2xl h-screen justify-center">
        <h2 className="text-2xl font-bold text-primary text-center">Карточка {String.fromCharCode(65 + currentCard)}</h2>
        <p className="text-lg text-center text-muted-foreground max-w-xl">{cards[currentCard].instruction}</p>
        {status === 'speaking' && <Volume2 className="size-12 text-accent animate-pulse" />}
        {status !== 'speaking' && (
          <button onClick={handleStart} className="px-8 py-4 bg-green-500 text-white rounded-2xl font-bold text-lg flex items-center gap-2 hover:bg-green-600">
            <Play /> Начать
          </button>
        )}
      </div>
    );
  }

  if (phase === 'running') {
    const card = cards[currentCard];
    const isLong = card.data.length > 60; // карточка A — компактнее, чтобы не скроллить
    const cellCls = isLong
      ? 'rounded-lg h-9 flex items-center justify-center font-bold whitespace-nowrap leading-none text-[clamp(10px,1.15vw,15px)]'
      : 'rounded-lg h-14 flex items-center justify-center font-bold whitespace-nowrap leading-none text-[clamp(12px,1.4vw,18px)]';

    return (
      <div className="flex flex-col items-center gap-4 w-full max-w-6xl h-screen justify-center py-4">
        <div className="flex justify-between w-full items-center">
          <h2 className="text-xl font-bold text-primary">Карточка {String.fromCharCode(65 + currentCard)}</h2>
          <div className="flex items-center gap-2 text-red-500 font-bold">
            <Mic className="size-6" /> Слушаю...
          </div>
        </div>

        {/* Вся карточка видна целиком — как бумажный бланк */}
        <div
          className="w-full max-h-[74vh] overflow-y-auto bg-white rounded-2xl border-2 border-border shadow-lg p-3"
          style={{ cursor: 'crosshair' }}
        >
          <div className="grid grid-cols-5 md:grid-cols-10" style={{ rowGap: '10px', columnGap: '6px' }}>
            {card.data.map((item, i) => {
              let style = {};
              let content = '';

              if (card.type === 'words') {
                content = item.word.toUpperCase();
                style.color = 'black';
              } else if (card.type === 'squares') {
                content = <img src={colorImg[item.color]} alt={item.color} className="w-full h-full object-contain rounded-md" />;
              } else if (card.type === 'interference') {
                content = item.word.toUpperCase();
                style.color = colorHex[item.color];
              }

              return (
                <div key={i} className={`${cellCls} ${card.type !== 'squares' ? 'border border-gray-200' : ''}`} style={style}>
                  {content}
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground/50">микрофон активен · распознано {currentIndex}/{card.data.length}</p>

        <button
          onClick={() => finishCard('manual')}
          title="Завершить карточку вручную (если тест завис)"
          className="absolute bottom-2 left-2 px-2.5 py-1 text-[10px] text-muted-foreground/40 border border-border/40 rounded-md hover:text-primary hover:border-primary/40 z-20"
        >
          дальше
        </button>
      </div>
    );
  }

  return null;
}